import React, { FC, RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DomPurify from 'dompurify';
import { useHistory, useLocation } from 'react-router-dom';
import { ActionTypes, PushNotification } from '../../../models/PushNotification';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import useIntersectionObserver from '../../../hooks/useIntersectionObserver';
import { useDateLocalization } from '../../../hooks/useDateLocalization';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { getErrorMessage } from '../../../utils';
import { ButtonClassNames, GlobalRouteNames, TopUpRouteNames, QueryParams } from '../../../models/Common';
import { DateVOv2 } from '../../../core/DateVOv2';

interface NotificationProps extends PushNotification {
  createdAt: string | null;
  firstUnreadNotificationId: number | null;
  setSeenNotificationsId: (ids: string[]) => void;
  seenNotificationsId: string[] | [];
  newMessageRef: RefObject<HTMLDivElement>;
  handleClose: () => void;
}

export const Notification: FC<NotificationProps> = ({
  ReadDate,
  id,
  createdAt,
  CreateDate,
  Image,
  Title,
  Message,
  firstUnreadNotificationId,
  setSeenNotificationsId,
  seenNotificationsId,
  newMessageRef,
  ExternalLink,
  ButtonName,
  Button,
  Action,
  handleClose,
}) => {
  const { t } = useTranslation();
  const { formatDate } = useDateLocalization();
  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, { rootMargin: '0px' });
  const isVisible = !!entry?.isIntersecting;
  const currentYear = new DateVOv2().getCurrentYear();
  const sanitizer = DomPurify.sanitize;
  const location = useLocation();
  const history = useHistory();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [showFile, { isFetching: isShowing }] = userInfoApi.useLazyShowUserDocumentQuery();

  const showFileHandler = async (url: string) => {
    const newTab = window.open('', '_blank');
    try {
      const fileData = await showFile(url.split('/').pop() as string).unwrap();
      const blob = new Blob([fileData], { type: 'application/pdf' });
      const objURL = window.URL.createObjectURL(blob);
      if (newTab) newTab.location.href = objURL;
      setTimeout(() => window.URL.revokeObjectURL(objURL), 8000);
    } catch (e) {
      newTab?.close();
      const message = getErrorMessage(e);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleMore = (link?: string) => {
    if (link) window.open(link, '_blank');
  };

  const handleAction = (path: string, search?: string, replace?: boolean) => {
    if (replace) {
      history.replace({ pathname: path, search: search || '' });
    } else {
      history.push({ pathname: path, search: search || '' });
    }
    handleClose();
  };

  useEffect(() => {
    if (isVisible && !ReadDate) {
      setSeenNotificationsId([...seenNotificationsId, `${id}`]);
    }
  }, [isVisible]);

  useEffect(() => {
    if (firstUnreadNotificationId === id && newMessageRef.current) {
      newMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const getActionButton = () => {
    switch (true) {
      case Button?.action === ActionTypes.TOP_UP: {
        return (
          <CustomMUIButton
            label={Button?.title || t('buttons.topUpBalance')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() =>
              handleAction(
                `${GlobalRouteNames.TopUpBalancePage}${TopUpRouteNames.BankCard}`,
                Button?.params?.amount ? `?amount=${Button?.params?.amount}` : '',
              )
            }
          />
        );
      }
      case Button?.action === ActionTypes.OPEN_DOCUMENT: {
        return (
          <CustomMUIButton
            label={Button?.title || t('buttons.more')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() => showFileHandler(`${Button?.params?.url}`)}
            isLoading={isShowing}
          />
        );
      }
      case Button?.action === ActionTypes.PAY_SERVICE: {
        return (
          <CustomMUIButton
            label={Button?.title || t('buttons.proceedToCheckout')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() =>
              handleAction(
                GlobalRouteNames.ServicePayment,
                `?${QueryParams.AccountId}=${Button?.params?.accountId}&${QueryParams.AccountType}=${
                  Button?.params?.accountType
                }&${QueryParams.ServiceId}=${Button?.params?.serviceId}&${QueryParams.IsChangeService}=${!!Button
                  ?.params?.isChangeService}`,
                location.pathname === GlobalRouteNames.ServicePayment,
              )
            }
          />
        );
      }
      case Button?.action === ActionTypes.PAYMENTS_HISTORY: {
        return (
          <CustomMUIButton
            label={Button?.title || t('paymentHistory.yourPayments')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() =>
              handleAction(
                GlobalRouteNames.PaymentHistory,
                createdAt ? `?period=${formatDate(createdAt, { year: 'numeric' })}` : '',
                location.pathname === GlobalRouteNames.PaymentHistory,
              )
            }
          />
        );
      }
      case Button?.action === ActionTypes.OPEN_LINK: {
        if (Button?.params?.url?.includes(GlobalRouteNames.SurveyPage)) {
          const url = new URL(Button?.params?.url);
          return (
            <CustomMUIButton
              label={Button?.title || t('notifications.moreDetails')}
              className={ButtonClassNames.PrimaryFulfilled}
              handleClick={() =>
                handleAction(GlobalRouteNames.SurveyPage, url.search, location.pathname === GlobalRouteNames.SurveyPage)
              }
            />
          );
        }
        return (
          <CustomMUIButton
            label={Button?.title || t('notifications.moreDetails')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() => handleMore(Button?.params?.url)}
          />
        );
      }
      case Action === ActionTypes.PAYMENTS_HISTORY: {
        return (
          <CustomMUIButton
            label={t('paymentHistory.yourPayments')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() =>
              handleAction(
                GlobalRouteNames.PaymentHistory,
                createdAt ? `?period=${formatDate(createdAt, { year: 'numeric' })}` : '',
                location.pathname === GlobalRouteNames.PaymentHistory,
              )
            }
          />
        );
      }
      case Boolean(ExternalLink): {
        return (
          <CustomMUIButton
            label={ButtonName || t('notifications.moreDetails')}
            className={ButtonClassNames.PrimaryFulfilled}
            handleClick={() => handleMore(String(ExternalLink))}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <div key={id} className="notification" ref={ref}>
      <div className="notification__header">
        {CreateDate && (
          <p className="notification__header__creation-date">
            {CreateDate?.includes(String(currentYear))
              ? formatDate(CreateDate, { day: 'numeric', month: 'long' })
              : formatDate(CreateDate, { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        )}
        {firstUnreadNotificationId === id && (
          <div className="notification__header__new-messages" ref={newMessageRef}>
            {t('notifications.newNotifications')}
          </div>
        )}
        {Image && <img src={Image} alt={Title} />}
      </div>
      <div className="notification__info">
        {createdAt && (
          <p className="notification__info__created-at">
            {formatDate(createdAt, { day: 'numeric', month: 'long', year: 'numeric' })}{' '}
            {formatDate(createdAt, { hour: 'numeric', minute: 'numeric' })}
          </p>
        )}
        <p className="notification__info__title">{Title}</p>
        <p
          className="notification__info__message"
          dangerouslySetInnerHTML={{ __html: sanitizer(Message ? Message.replace(/\n/g, '<br>') : '') }}
        />
      </div>
      {(Button?.action || Action || ExternalLink) && getActionButton()}
    </div>
  );
};
